import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';

export const AlertReportsTab = ({
  alertLogs,
  selectedAlertLogs,
  setSelectedAlertLogs,
  onBulkDelete,
  onExport,
  onShowHistory,
  onRefresh
}) => {
  return (
    <div className="space-y-4">
      <h2 className="text-3xl text-red-600 mb-4" 
          style={{ 
            fontFamily: 'Bebas Neue, sans-serif',
            letterSpacing: '4px',
            textShadow: 'none',
            fontWeight: '400'
          }}>
        ALERT REPORTS
      </h2>

      <div className="p-4 bg-blue-50 border-4 border-blue-400 rounded mb-4">
        <p className="text-sm text-gray-700">
          ℹ️ <strong>Info:</strong> View and export a log of all urgent alerts triggered by users. 
          Download as spreadsheet to analyze alert patterns.
        </p>
      </div>

      <div className="flex gap-3 mb-4 flex-wrap">
        {selectedAlertLogs.length > 0 && (
          <Button
            onClick={onBulkDelete}
            className="bg-red-600 hover:bg-red-700 text-white font-bold py-3 px-6 border-3 border-black"
            style={{
              fontFamily: 'Fredoka, sans-serif',
              letterSpacing: '0.05em',
              textTransform: 'uppercase'
            }}
          >
            🗑️ Delete Selected ({selectedAlertLogs.length})
          </Button>
        )}
        <Button
          onClick={onExport}
          className="bg-green-600 hover:bg-green-700 text-white font-bold py-3 px-6 border-3 border-black"
          style={{
            fontFamily: 'Fredoka, sans-serif',
            letterSpacing: '0.05em',
            textTransform: 'uppercase'
          }}
          data-testid="export-alert-logs-csv"
        >
          📥 Export CSV
        </Button>
        <Button
          onClick={onShowHistory}
          className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-6 border-3 border-black"
          style={{
            fontFamily: 'Fredoka, sans-serif',
            letterSpacing: '0.05em',
            textTransform: 'uppercase'
          }}
        >
          📋 View Export History
        </Button>
        <Button
          onClick={onRefresh}
          className="bg-gray-600 hover:bg-gray-700 text-white font-bold py-3 px-6 border-3 border-black"
          style={{
            fontFamily: 'Fredoka, sans-serif',
            letterSpacing: '0.05em',
            textTransform: 'uppercase'
          }}
        >
          🔄 Refresh
        </Button>
      </div>

      {/* Alert Logs Table */}
      <div className="bg-white border-4 border-black rounded overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-red-600 text-white">
              <tr>
                <th className="px-4 py-3 text-left w-12">
                  <Checkbox
                    checked={selectedAlertLogs.length === alertLogs.length && alertLogs.length > 0}
                    onCheckedChange={(checked) => {
                      if (checked) {
                        setSelectedAlertLogs(alertLogs.map(l => l.id));
                      } else {
                        setSelectedAlertLogs([]);
                      }
                    }}
                    className="border-white"
                  />
                </th>
                <th className="px-4 py-3 text-left font-bold" style={{ fontFamily: 'Fredoka, sans-serif' }}>Date & Time</th>
                <th className="px-4 py-3 text-left font-bold" style={{ fontFamily: 'Fredoka, sans-serif' }}>Triggered By</th>
                <th className="px-4 py-3 text-left font-bold" style={{ fontFamily: 'Fredoka, sans-serif' }}>Restaurant</th>
                <th className="px-4 py-3 text-left font-bold" style={{ fontFamily: 'Fredoka, sans-serif' }}>Alert Type</th>
                <th className="px-4 py-3 text-left font-bold" style={{ fontFamily: 'Fredoka, sans-serif' }}>Status</th>
              </tr>
            </thead>
            <tbody>
              {alertLogs.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-4 py-8 text-center text-gray-500">
                    No alerts have been triggered yet.
                  </td>
                </tr>
              ) : (
                alertLogs.map((log, index) => (
                  <tr key={log.id || index} className={index % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                    <td className="px-4 py-3">
                      <Checkbox
                        checked={selectedAlertLogs.includes(log.id)}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            setSelectedAlertLogs([...selectedAlertLogs, log.id]);
                          } else {
                            setSelectedAlertLogs(selectedAlertLogs.filter(id => id !== log.id));
                          }
                        }}
                      />
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {new Date(log.triggered_at).toLocaleString()}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <div className="font-bold">{log.triggered_by_user_name}</div>
                      <div className="text-gray-500 text-xs">{log.triggered_by_user_email}</div>
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <div className="font-bold">{log.restaurant_name}</div>
                      <div className="text-gray-500 text-xs">{log.restaurant_phone}</div>
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <span className={`px-2 py-1 rounded text-xs font-bold ${
                        log.alert_type === 'needs_attention' 
                          ? 'bg-orange-100 text-orange-800' 
                          : 'bg-blue-100 text-blue-800'
                      }`}>
                        {log.alert_label || log.alert_type}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <span className={`px-2 py-1 rounded text-xs font-bold ${
                        log.status === 'initiated' 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {log.status}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <p className="text-sm text-gray-600 mt-2">
        Total alerts: <strong>{alertLogs.length}</strong>
      </p>
    </div>
  );
};
